import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Instagram } from "lucide-react";

export default function Header() {
  const { lang, setLang, t, langs } = useI18n();
  const { count } = useCart();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <header
      className="sticky top-0 z-30 border-b border-border/60 backdrop-blur-xl"
      style={{ background: "rgba(247,245,240,0.82)" }}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-8">
        <Link
          to="/"
          className="font-hand text-4xl leading-none tracking-tight flex items-center gap-3"
          data-testid="brand-logo"
        >
          <span
            className="w-10 h-10 rounded-full overflow-hidden border-2 border-[hsl(var(--primary))]/40 shrink-0 hidden sm:inline-block"
            aria-hidden="true"
          >
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_ceramic-shop-18/artifacts/hntemrqm_Pikkurumiluksia2.webp"
              alt=""
              className="w-full h-full object-cover"
            />
          </span>
          <span className="flex items-baseline">
            <span className="text-[hsl(var(--primary))]">@</span>pikkurumiluksia
          </span>
        </Link>

        {!isAdmin && (
          <nav className="hidden md:flex items-center gap-10">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `label-caps link-underline ${isActive ? "text-foreground" : "text-muted-foreground"}`
              }
              data-testid="nav-home"
            >
              {t("nav.home")}
            </NavLink>
            <NavLink
              to="/shop"
              className={({ isActive }) =>
                `label-caps link-underline ${isActive ? "text-foreground" : "text-muted-foreground"}`
              }
              data-testid="nav-shop"
            >
              {t("nav.shop")}
            </NavLink>
            <NavLink
              to="/story"
              className={({ isActive }) =>
                `label-caps link-underline ${isActive ? "text-foreground" : "text-muted-foreground"}`
              }
              data-testid="nav-story"
            >
              {t("nav.story")}
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 label-caps" data-testid="language-switcher">
            {langs.map((l, i) => (
              <div key={l} className="flex items-center gap-2">
                <button
                  onClick={() => setLang(l)}
                  className={`transition-colors ${
                    l === lang
                      ? "text-foreground underline underline-offset-[6px] decoration-1"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`lang-btn-${l}`}
                >
                  {l.toUpperCase()}
                </button>
                {i < langs.length - 1 && <span className="text-muted-foreground/40">|</span>}
              </div>
            ))}
          </div>

          <a
            href="https://instagram.com/pikkurumiluksia"
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
            aria-label="Instagram"
            data-testid="header-ig-link"
          >
            <Instagram strokeWidth={1.5} className="w-4 h-4" />
          </a>

          <Link
            to="/cart"
            className="relative flex items-center gap-2 label-caps"
            data-testid="cart-link"
          >
            <ShoppingBag strokeWidth={1.25} className="w-5 h-5" />
            <span className="hidden sm:inline">{t("nav.cart")}</span>
            {count > 0 && (
              <span
                className="ml-1 min-w-5 h-5 px-1 inline-flex items-center justify-center bg-[hsl(var(--primary))] text-white text-[10px] tracking-normal"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}
